import type {
  BooleanFilterOperator,
  DateFilterOperator,
  FilterOperator,
  NumericFilterOperator,
  SelectionListFilterOperator,
  StringFilterOperator,
} from '../../data-builder.store';

const STRING_OPERATOR_LABELS: Record<StringFilterOperator, string> = {
  EQUAL_TO: 'Equal to',
  DOES_NOT_EQUAL: 'Does not equal',
  CONTAINS_AT_LEAST_ONE: 'Contains at least one',
  CONTAINS_AT_LEAST_ONE_CASE_SENSITIVE:
    'Contains at least one (case sensitive)',
  CONTAINS_ALL: 'Contains all',
  CONTAINS_ALL_CASE_SENSITIVE: 'Contains all (case sensitive)',
  DOES_NOT_CONTAIN_AT_LEAST_ONE: 'Does not contain at least one',
  DOES_NOT_CONTAIN_AT_LEAST_ONE_CASE_SENSITIVE:
    'Does not contain at least one (case sensitive)',
  DOES_NOT_CONTAIN_ALL: 'Does not contain all',
  DOES_NOT_CONTAIN_ALL_CASE_SENSITIVE: 'Does not contain all (case sensitive)',
  CONTAINS_AT_LEAST_ONE_EXACT: 'Contains at least one (exact)',
  CONTAINS_ALL_EXACT: 'Contains all (exact)',
  DOES_NOT_CONTAIN_AT_LEAST_ONE_EXACT: 'Does not contain at least one (exact)',
  DOES_NOT_CONTAIN_ALL_EXACT: 'Does not contain all (exact)',
  IS_NULL: 'Is null',
  IS_NOT_NULL: 'Is not null',
};

const SELECTION_LIST_OPERATOR_LABELS: Record<
  SelectionListFilterOperator,
  string
> = {
  IN_SET: 'In set',
  NOT_IN_SET: 'Not in set',
  IS_NULL: 'Is null',
  IS_NOT_NULL: 'Is not null',
};

const NUMERIC_OPERATOR_LABELS: Record<NumericFilterOperator, string> = {
  GREATER_THAN: 'Greater than',
  GREATER_THAN_OR_EQUAL: 'Greater than or equal',
  LESS_THAN: 'Less than',
  LESS_THAN_OR_EQUAL: 'Less than or equal',
  IN_BETWEEN: 'In between',
  IS_NULL: 'Is null',
  IS_NOT_NULL: 'Is not null',
};

const DATE_OPERATOR_LABELS: Record<DateFilterOperator, string> = {
  GREATER_THAN_OR_EQUAL: 'Greater than or equal',
  LESS_THAN_OR_EQUAL: 'Less than or equal',
  IN_BETWEEN: 'In between',
  IS_NULL: 'Is null',
  IS_NOT_NULL: 'Is not null',
};

const BOOLEAN_OPERATOR_LABELS: Record<BooleanFilterOperator, string> = {
  IS_TRUE: 'Is true',
  IS_FALSE: 'Is false',
  IS_NULL: 'Is null',
  IS_NOT_NULL: 'Is not null',
};

function humanizeOperatorKey(key: string): string {
  return key.toLowerCase().replace(/_/g, ' ');
}

export function getOperatorLabel(operator: string): string {
  return (
    (STRING_OPERATOR_LABELS as Record<string, string>)[operator] ??
    (SELECTION_LIST_OPERATOR_LABELS as Record<string, string>)[operator] ??
    (DATE_OPERATOR_LABELS as Record<string, string>)[operator] ??
    (BOOLEAN_OPERATOR_LABELS as Record<string, string>)[operator] ??
    (NUMERIC_OPERATOR_LABELS as Record<string, string>)[operator] ??
    humanizeOperatorKey(operator)
  );
}

export function isEqualityOperator(operator: FilterOperator | null): boolean {
  return operator === 'EQUAL_TO' || operator === 'DOES_NOT_EQUAL';
}

export function isSelectionListOperator(
  operator: FilterOperator | null
): boolean {
  return operator === 'IN_SET' || operator === 'NOT_IN_SET';
}

export function isInBetweenOperator(operator: FilterOperator | null): boolean {
  return operator === 'IN_BETWEEN';
}

export function isNullaryOperator(operator: FilterOperator | null): boolean {
  return operator === 'IS_NULL' || operator === 'IS_NOT_NULL';
}

export function getOperatorSelectOptions(operators: FilterOperator[]) {
  return operators.map((op) => ({
    label: getOperatorLabel(op),
    value: op,
  }));
}
